/**
 * GoogleSheetsServiceCard - карточка табеля аптеки в Google Таблицах.
 * Выдержана в единой стилистике страницы Профиля (фон #f8fafc, четкие кнопки).
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  TableChartOutlined,
  RestartAlt,
  OpenInNew,
  CheckCircle,
  ErrorOutline
} from '@mui/icons-material';
import axios from 'axios';
import { notify } from '../../../notifications';
import { serviceButtonFullSx, serviceButtonGrowSx, serviceCardSx } from './serviceCardStyles';

export interface GoogleSheetsServiceCardProps {
  pharmacyNum?: string | null;
}

const GOOGLE_API_CONFIG = {
  clientId: '781945341153-s6b2jrk76knme2hvjqveka00qui1lu1j.apps.googleusercontent.com',
  clientSecret: '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',
  refreshToken: '1//043-eWXl92KIMCgYIARAAGAQSNwF-L9Ir7JcfcVt1kcaxYGKR3whplZ8qMsAt2WNNNebrzfS-1tUswRs9bTw1UfrbaJrNL2YQTo0'
};

const GoogleSheetsServiceCard: React.FC<GoogleSheetsServiceCardProps> = ({
  pharmacyNum
}) => {
  const [openingSheet, setOpeningSheet] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ success: boolean; text: string } | null>(null);

  const getFolderId = async (): Promise<string> => {
    const cookieName = 'timesheet_folder_id';
    const matches = document.cookie.match(new RegExp('(?:^|; )' + cookieName + '=([^;]*)'));
    const cachedFolderId = matches ? decodeURIComponent(matches[1]) : null;
    if (cachedFolderId) {
      return cachedFolderId;
    }

    const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/timesheet/folderID');
    if (response.data && response.data.success && response.data.folderId) {
      const folderId = response.data.folderId;
      const expires = new Date(new Date().getFullYear(), 11, 31, 23, 59, 59);
      document.cookie = `${cookieName}=${folderId}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
      return folderId;
    }
    throw new Error('Идентификатор папки не получен с сервера');
  };

  const getAccessToken = async (): Promise<string> => {
    const response = await axios.post('https://oauth2.googleapis.com/token', {
      client_id: GOOGLE_API_CONFIG.clientId,
      client_secret: GOOGLE_API_CONFIG.clientSecret,
      refresh_token: GOOGLE_API_CONFIG.refreshToken,
      grant_type: 'refresh_token'
    });
    return response.data.access_token;
  };

  const fetchPharmacySheet = async (pNum: string) => {
    const folderId = await getFolderId();
    const token = await getAccessToken();
    const res = await axios.get('https://www.googleapis.com/drive/v3/files', {
      params: {
        q: `'${folderId}' in parents and mimeType='application/vnd.google-apps.spreadsheet'`,
        fields: 'files(id, name, webViewLink)',
        pageSize: 1000
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const files = res.data.files || [];
    const possibleSheetNames = [
      `Табель аптека №${pNum}_`,
      `Табель Аптека №${pNum}_`,
      `Табель аптека №${pNum}`,
      `Табель Аптека №${pNum}`
    ];
    return files.find((f: { name?: string; webViewLink?: string }) =>
      possibleSheetNames.includes((f.name || '').trim())
    );
  };

  // Сбросить кэш папки табелей
  const handleResetTimesheet = () => {
    document.cookie = 'timesheet_folder_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    localStorage.removeItem('timesheet_folder_id');
    setStatusMessage(null);
    notify.success('Кэш табеля сброшен. При следующем открытии ссылка обновится заново.');
  };

  // Открыть таблицу этой аптеки
  const handleOpenSheet = async () => {
    if (!pharmacyNum || pharmacyNum === 'NaN' || pharmacyNum === '-') {
      notify.warning('Номер аптеки не указан в вашем профиле');
      return;
    }

    setOpeningSheet(true);
    setStatusMessage(null);
    try {
      const sheet = await fetchPharmacySheet(pharmacyNum);
      if (sheet && sheet.webViewLink) {
        window.open(sheet.webViewLink, '_blank', 'noopener,noreferrer');
      } else {
        setStatusMessage({
          success: false,
          text: `Табель для аптеки №${pharmacyNum} не найден на диске. Обратитесь в IT-отдел.`
        });
        notify.error(`Табель для аптеки №${pharmacyNum} не найден`);
      }
    } catch (err) {
      console.error('Ошибка открытия Google Таблицы:', err);
      setStatusMessage({
        success: false,
        text: 'Не удалось подключиться к Google Таблицам. Попробуйте нажать «Сбросить кэш».'
      });
      notify.error('Не удалось открыть табель');
    } finally {
      setOpeningSheet(false);
    }
  };

  const isAssignedPharmacy = pharmacyNum && pharmacyNum !== 'NaN' && pharmacyNum !== '-';

  return (
    <Box sx={{ ...serviceCardSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
        {/* Левая часть: Иконка + Название + Чип аптеки (кнопки уходят на следующую строку, когда колонка узкая) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 280px', minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: 'rgba(15, 157, 88, 0.1)',
              color: '#0f9d58',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <TableChartOutlined sx={{ fontSize: 24 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem', lineHeight: 1.35 }}>
                Табель рабочего времени (Google Таблицы)
              </Typography>
              <Chip
                label={isAssignedPharmacy ? `Аптека №${pharmacyNum}` : 'Аптека не привязана'}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.74rem',
                  height: 22,
                  bgcolor: '#ffffff',
                  color: isAssignedPharmacy ? '#166534' : '#64748b',
                  border: `1px solid ${isAssignedPharmacy ? '#bbf7d0' : '#e2e8f0'}`
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '0.85rem' }}>
              Электронный табель вашей аптеки. Нажмите зеленую кнопку, чтобы открыть его в новой вкладке.
            </Typography>
          </Box>
        </Box>

        {/* Правая часть: Две простые кнопки */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.25,
            ...serviceButtonFullSx
          }}
        >
          {/* Главная кнопка: Открыть табель */}
          <Button
            variant="contained"
            startIcon={openingSheet ? <CircularProgress size={16} color="inherit" /> : <OpenInNew sx={{ fontSize: 18 }} />}
            disabled={openingSheet}
            onClick={handleOpenSheet}
            sx={{
              bgcolor: '#0f9d58',
              color: '#ffffff',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              py: 0.85,
              px: 2.25,
              whiteSpace: 'nowrap',
              ...serviceButtonGrowSx,
              '&:hover': {
                bgcolor: '#0b8043'
              }
            }}
          >
            {openingSheet ? 'Открытие...' : 'Открыть табель'}
          </Button>

          {/* Вторичная кнопка: Сбросить кэш */}
          <Tooltip title="Нажмите, если табель перестал открываться или завис" arrow>
            <Button
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={handleResetTimesheet}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                py: 0.85,
                px: 1.5,
                borderColor: '#cbd5e1',
                bgcolor: '#ffffff',
                color: '#334155',
                whiteSpace: 'nowrap',
                ...serviceButtonGrowSx,
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f1f5f9'
                }
              }}
            >
              Сбросить кэш
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Оповещение об ошибке */}
      {statusMessage && (
        <Box
          sx={{
            p: 1.5,
            px: 2,
            borderRadius: '10px',
            bgcolor: statusMessage.success ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${statusMessage.success ? '#bbf7d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25
          }}
        >
          {statusMessage.success ? (
            <CheckCircle sx={{ color: '#16a34a', fontSize: 18 }} />
          ) : (
            <ErrorOutline sx={{ color: '#dc2626', fontSize: 18 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: statusMessage.success ? '#166534' : '#991b1b',
              fontSize: '0.84rem',
              fontWeight: 500
            }}
          >
            {statusMessage.text}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GoogleSheetsServiceCard;
